import type { AtlasSourceDescriptor, WorldAtlasAssetProvider } from './assetProvider';
import type {
  AtlasAnimationGroup,
  AtlasAssetKind,
  AtlasFrame,
  AtlasManifest,
  AtlasPage,
} from './atlasTypes';
import { RuntimeAtlasAssetProvider } from './runtimeAssetProvider';

interface PrebuiltAtlasPageRecord {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly imagePath: string;
}

interface PrebuiltAtlasManifestRecord {
  readonly source: 'prebuilt';
  readonly builtAtMs: number;
  readonly sourceSignature?: string;
  readonly pages: readonly PrebuiltAtlasPageRecord[];
  readonly frames: readonly AtlasFrame[];
  readonly animationGroups: readonly AtlasAnimationGroup[];
}

interface PrebuiltWorldAtlasAssetProviderOptions {
  readonly sourceFactory: () => readonly AtlasSourceDescriptor[];
  readonly manifestPath: string;
  readonly pageSize?: number;
  readonly padding?: number;
}

type ResolvedManifest = AtlasManifest & { readonly source: 'prebuilt' | 'runtime' };

declare global {
  interface Window {
    __downloadWorld2DAtlas?: () => Promise<void>;
    __world2dAtlasExportInfo?: string;
  }
}

export class PrebuiltWorldAtlasAssetProvider implements WorldAtlasAssetProvider {
  private readonly runtimeProvider: RuntimeAtlasAssetProvider;
  private readonly sourceFactory: () => readonly AtlasSourceDescriptor[];
  private readonly manifestPath: string;
  private readonly sourceSignature: string;
  private manifest: ResolvedManifest | null = null;
  private pendingManifest: Promise<ResolvedManifest> | null = null;
  private readonly pageById = new Map<string, AtlasPage>();
  private readonly frameById = new Map<string, AtlasFrame>();
  private readonly animationGroupById = new Map<string, AtlasAnimationGroup>();
  private readonly terrainFrameBySourceId = new Map<string, AtlasFrame>();
  private readonly objectFrameBySourceId = new Map<string, AtlasFrame>();

  constructor(options: PrebuiltWorldAtlasAssetProviderOptions) {
    this.sourceFactory = options.sourceFactory;
    this.manifestPath = options.manifestPath;
    this.sourceSignature = createAtlasSourceSignature(this.sourceFactory());
    this.runtimeProvider = new RuntimeAtlasAssetProvider({
      sourceFactory: this.sourceFactory,
      pageSize: options.pageSize,
      padding: options.padding,
    });
  }

  public async ensureReady(): Promise<void> {
    if (this.manifest) {
      return;
    }
    if (!this.pendingManifest) {
      this.pendingManifest = this.loadManifest()
        .then((manifest) => {
          this.installManifest(manifest);
          return manifest;
        })
        .finally(() => {
          this.pendingManifest = null;
        });
    }
    await this.pendingManifest;
  }

  public isReady(): boolean {
    return this.manifest !== null;
  }

  public getManifest(): AtlasManifest | null {
    return this.manifest;
  }

  public getPage(pageId: string): AtlasPage | null {
    return this.pageById.get(pageId) ?? null;
  }

  public getFrame(frameId: string): AtlasFrame | null {
    return this.frameById.get(frameId) ?? null;
  }

  public getTerrainFrame(terrainId: string): AtlasFrame | null {
    return this.terrainFrameBySourceId.get(terrainId) ?? null;
  }

  public getObjectFrame(assetId: string): AtlasFrame | null {
    return this.objectFrameBySourceId.get(assetId) ?? null;
  }

  public getAnimationGroup(groupId: string): AtlasAnimationGroup | null {
    return this.animationGroupById.get(groupId) ?? null;
  }

  public listFrames(kind?: AtlasAssetKind): readonly AtlasFrame[] {
    const frames = [...this.frameById.values()];
    return typeof kind === 'string' ? frames.filter((frame) => frame.kind === kind) : frames;
  }

  private async loadManifest(): Promise<ResolvedManifest> {
    const prebuilt = await this.tryLoadPrebuiltManifest();
    if (prebuilt) {
      console.info(
        '[World2DAtlas] Using prebuilt atlas manifest.',
        {
          pageCount: prebuilt.pages.length,
          frameCount: prebuilt.frames.length,
          animatedFrameCount: prebuilt.frames.filter((frame) => frame.animated).length,
          animationGroupCount: prebuilt.animationGroups.length,
        },
      );
      return prebuilt;
    }

    console.info('[World2DAtlas] Falling back to runtime atlas generation.');
    await this.runtimeProvider.ensureReady();
    const runtimeManifest = this.runtimeProvider.getManifest();
    if (!runtimeManifest) {
      throw new Error('Runtime atlas manifest was not available after generation.');
    }
    this.installRuntimeExportHook(runtimeManifest);
    return runtimeManifest as ResolvedManifest;
  }

  private async tryLoadPrebuiltManifest(): Promise<ResolvedManifest | null> {
    let response: Response;
    try {
      response = await fetch(this.manifestPath, { cache: 'no-cache' });
    } catch {
      console.info('[World2DAtlas] Prebuilt atlas manifest fetch failed.', { manifestPath: this.manifestPath });
      return null;
    }
    if (!response.ok) {
      console.info('[World2DAtlas] Prebuilt atlas manifest request was not OK.', {
        manifestPath: this.manifestPath,
        status: response.status,
      });
      return null;
    }

    const record = (await response.json()) as PrebuiltAtlasManifestRecord;
    if (!Array.isArray(record.pages) || !Array.isArray(record.frames) || !Array.isArray(record.animationGroups)) {
      console.info('[World2DAtlas] Prebuilt atlas manifest shape was invalid.', {
        manifestPath: this.manifestPath,
      });
      return null;
    }
    if ((record.sourceSignature ?? null) !== this.sourceSignature) {
      console.info('[World2DAtlas] Prebuilt atlas source signature mismatch; ignoring prebuilt atlas.', {
        manifestPath: this.manifestPath,
        expectedSourceSignature: this.sourceSignature,
        actualSourceSignature: record.sourceSignature ?? null,
      });
      return null;
    }

    const pages = await Promise.all(record.pages.map(async (page): Promise<AtlasPage> => ({
      id: page.id,
      width: page.width,
      height: page.height,
      image: await loadImage(page.imagePath),
    })));

    return {
      source: 'prebuilt',
      builtAtMs: record.builtAtMs,
      pages,
      frames: record.frames,
      animationGroups: record.animationGroups,
    };
  }

  private installManifest(manifest: ResolvedManifest): void {
    this.manifest = manifest;
    this.pageById.clear();
    this.frameById.clear();
    this.animationGroupById.clear();
    this.terrainFrameBySourceId.clear();
    this.objectFrameBySourceId.clear();

    for (const page of manifest.pages) {
      this.pageById.set(page.id, page);
    }

    for (const frame of manifest.frames) {
      this.frameById.set(frame.id, frame);
      if (frame.kind === 'terrain') {
        this.terrainFrameBySourceId.set(frame.sourceId, frame);
      } else if (frame.kind === 'object') {
        this.objectFrameBySourceId.set(frame.sourceId, frame);
      }
    }

    for (const group of manifest.animationGroups) {
      this.animationGroupById.set(group.id, group);
    }
  }

  private installRuntimeExportHook(manifest: AtlasManifest): void {
    const pageNames = manifest.pages.map((_, index) => `page-${index}.png`);
    const targetDir = 'frontend/public/visual-assets/world2d-atlas/default/';
    const infoLines = [
      '[World2DAtlas] No prebuilt atlas found. Runtime atlas fallback is active.',
      `[World2DAtlas] Save exported files under ${targetDir}`,
      ...pageNames.map((name) => `[World2DAtlas] Page file: ${name}`),
      '[World2DAtlas] Manifest file: manifest.json',
      '[World2DAtlas] Run window.__downloadWorld2DAtlas() to download the exact runtime-generated atlas files.',
    ];
    const infoText = infoLines.join('\n');
    window.__world2dAtlasExportInfo = infoText;
    window.__downloadWorld2DAtlas = async () => {
      console.log(infoText);
      const pagePathById = new Map<string, string>();
      for (let index = 0; index < manifest.pages.length; index += 1) {
        const page = manifest.pages[index];
        const fileName = `page-${index}.png`;
        const dataUrl = page.image instanceof HTMLCanvasElement
          ? page.image.toDataURL('image/png')
          : await renderCanvasImageSourceToDataUrl(page.image, page.width, page.height);
        pagePathById.set(page.id, `/visual-assets/world2d-atlas/default/${fileName}`);
        triggerDownload(dataUrl, fileName);
      }

      const serializableManifest: PrebuiltAtlasManifestRecord = {
        source: 'prebuilt',
        builtAtMs: manifest.builtAtMs,
        sourceSignature: this.sourceSignature,
        pages: manifest.pages.map((page) => ({
          id: page.id,
          width: page.width,
          height: page.height,
          imagePath: pagePathById.get(page.id) ?? `/visual-assets/world2d-atlas/default/${page.id}.png`,
        })),
        frames: manifest.frames,
        animationGroups: manifest.animationGroups,
      };
      const manifestBlob = new Blob([JSON.stringify(serializableManifest, null, 2)], { type: 'application/json' });
      triggerDownload(URL.createObjectURL(manifestBlob), 'manifest.json', true);
    };
    console.log(infoText);
  }
}

function createAtlasSourceSignature(sources: readonly AtlasSourceDescriptor[]): string {
  return JSON.stringify(
    sources.map((source) => ({
      id: source.id,
      kind: source.kind,
      sourcePath: source.sourcePath,
      sourceWidth: source.sourceWidth ?? null,
      sourceHeight: source.sourceHeight ?? null,
      drawWidth: source.drawWidth,
      drawHeight: source.drawHeight,
      pivotX: source.pivotX ?? null,
      pivotY: source.pivotY ?? null,
      offsetX: source.offsetX ?? null,
      offsetY: source.offsetY ?? null,
      animated: source.animated === true,
      animationGroupId: source.animationGroupId ?? null,
      animationDurationMs: source.animationDurationMs ?? null,
    })),
  );
}

function triggerDownload(url: string, fileName: string, revokeAfter = false): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  if (revokeAfter) {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

async function renderCanvasImageSourceToDataUrl(image: CanvasImageSource, width: number, height: number): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to create export canvas.');
  }
  ctx.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/png');
}

function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load atlas page '${path}'.`));
    image.src = path;
  });
}
